import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingProps } from "../common";
import customsGlobeUrl from "./customs-information-assets/customs-globe.png?url";
import * as styles from "./customs-information-modal.module.css";

const heroImageProps = { alt: "", src: customsGlobeUrl };

export const CustomsInformationModal = memo(function CustomsInformationModal({
  handleDismiss,
  handleComplete,
}: OnboardingProps) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();

  const handleCompleteDetails = useHandler(() => {
    navigate(routes.getSettingsSubRoute("international/returns"));
    void handleComplete();
  });

  return (
    <RedoBaseModal
      darkMode
      isOpen
      onModalCloseRequested={handleDismiss}
      otherClasses={[styles.modal]}
    >
      <Flex className={styles.shell} dir="row" gap="none" w="full">
        <Flex
          className={styles.main}
          dir="column"
          flex={1}
          justify="space-between"
        >
          <Flex dir="column" gap="2xl" p="5xl">
            <Text
              className={styles.eyebrow}
              fontSize="xs"
              fontWeight="medium"
              textColor="secondary"
            >
              {t("Customs information")}
            </Text>
            <Text
              as="h2"
              fontFamily="serif"
              fontSize="4xl"
              fontWeight="regular"
              textColor="primary"
            >
              {t("Complete your customs information")}
            </Text>
            <Text fontSize="md" textColor="secondary">
              {t(
                "Returns may be stuck in customs because of missing information.",
              )}
            </Text>
            <Text fontSize="md" textColor="secondary">
              {t(
                "Update your store and catalog customs information to ensure all returns clear customs smoothly, without unexpected duties and taxes.",
              )}
            </Text>
          </Flex>
          <Flex dir="row" gap="md" p="3xl" wrap="wrap">
            <RedoButton
              hierarchy="secondary"
              onClick={() => void handleDismiss()}
              size="sm"
              text={t("Not now")}
            />
            <RedoButton
              hierarchy="primary"
              onClick={() => void handleCompleteDetails()}
              size="sm"
              text={t("Complete required details")}
              theme="action"
            />
          </Flex>
        </Flex>
        <Flex
          {...heroImageProps}
          aria-hidden
          as="img"
          className={styles.heroImage}
          shrink={0}
        />
      </Flex>
    </RedoBaseModal>
  );
});
